import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { buscarTituloCompleto, cargarProductosPorMarca } from '../utils/loaders';
import emptyAnimation from '../assets/empty.json';
import LottieAnimation from '../hooks/getLottie';

let timer;

const quickSearches = [
  { label: 'Motorola', esMarca: true },
  { label: 'Hytera', esMarca: true },
  { label: 'Kenwood', esMarca: true },
  { label: 'Repetidora', esMarca: false },
  { label: 'Accesorios', esMarca: false },
];

export default function HeaderInformativo() {
  const [query, setQuery] = useState('');
  const [resultadosBusqueda, setResultadosBusqueda] = useState([]);
  const [busquedaRealizada, setBusquedaRealizada] = useState(false);
  const [cargando, setCargando] = useState(false);
  const [focused, setFocused] = useState(false);
  const wrapperRef = useRef(null);

  const ejecutarBusqueda = (texto) => {
    clearTimeout(timer);
    if (!texto.trim()) {
      setResultadosBusqueda([]);
      setBusquedaRealizada(false);
      return;
    }
    timer = setTimeout(async () => {
      setCargando(true);
      const resultados = await buscarTituloCompleto(texto);
      setResultadosBusqueda(resultados.data || []);
      setBusquedaRealizada(true);
      setCargando(false);
    }, 300);
  };

  const handleInputChange = (e) => {
    setQuery(e.target.value);
    ejecutarBusqueda(e.target.value);
  };

  const handleQuickSearch = async (term, esMarca) => {
    setQuery(term);
    setFocused(true);
    setBusquedaRealizada(false);
    setCargando(true);
    setResultadosBusqueda([]);

    if (esMarca) {
      const res = await cargarProductosPorMarca(term, 12);
      setResultadosBusqueda(res.data || []);
    } else {
      const res = await buscarTituloCompleto(term);
      setResultadosBusqueda(res.data || []);
    }

    setBusquedaRealizada(true);
    setCargando(false);
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setFocused(false);
        setResultadosBusqueda([]);
        setBusquedaRealizada(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const stripHtmlTags = (input) => (input ? input.replace(/<\/?[^>]+(>|$)/g, '') : '');

  const showDropdown = focused && (cargando || resultadosBusqueda.length > 0 || busquedaRealizada);

  return (
    <div className="w-full bg-[#0a1e3d] border-b border-white/5">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center gap-4">

        {/* Contacto rápido — visible solo en md+ */}
        <div className="hidden md:flex items-center gap-5 shrink-0">
          <a
            href="mailto:contacto@atst.cl"
            className="flex items-center gap-2 text-gray-300 hover:text-[#00BEE7] transition-colors duration-200 text-sm font-medium group"
          >
            <span className="flex items-center justify-center w-7 h-7 rounded-full bg-white/10 group-hover:bg-[#00BEE7]/20 transition-colors duration-200">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </span>
            contacto@atst.cl
          </a>
        </div>

        {/* Buscador */}
        <div className="flex-1 relative" ref={wrapperRef}>
          <div className={`flex items-center rounded-2xl border-2 transition-all duration-200 overflow-hidden ${
            focused
              ? 'border-[#00BEE7] shadow-lg shadow-[#00BEE7]/20'
              : 'border-white/20 hover:border-white/40'
          }`} style={{ background: focused ? 'rgba(255,255,255,0.16)' : 'rgba(255,255,255,0.09)' }}>
            <span className="pl-4 pr-2.5 text-gray-400 shrink-0">
              {cargando ? (
                <svg className="h-5 w-5 animate-spin text-[#00BEE7]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M17 11A6 6 0 115 11a6 6 0 0112 0z" />
                </svg>
              )}
            </span>
            <input
              type="text"
              value={query}
              placeholder="Buscar radios, accesorios, marcas..."
              className="w-full bg-transparent py-3 pr-4 outline-none border-none focus:ring-0 text-white placeholder-gray-400 text-sm font-semibold"
              onChange={handleInputChange}
              onFocus={() => setFocused(true)}
            />
            {query && (
              <button
                type="button"
                onClick={() => { setQuery(''); setResultadosBusqueda([]); setBusquedaRealizada(false); }}
                className="pr-4 text-gray-400 hover:text-white transition-colors"
                aria-label="Limpiar búsqueda"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>

          {/* Búsquedas rápidas */}
          {focused && !query && (
            <div className="absolute top-full left-0 right-0 mt-2 bg-[#0f274b] border border-white/15 rounded-2xl shadow-2xl z-[9999] p-4">
              <p className="text-xs text-gray-500 font-bold uppercase tracking-widest mb-3 px-1">Búsquedas frecuentes</p>
              <div className="flex flex-wrap gap-2">
                {quickSearches.map(({ label, esMarca }) => (
                  <button
                    key={label}
                    type="button"
                    onClick={() => handleQuickSearch(label, esMarca)}
                    className="flex items-center gap-1.5 px-3 py-2 bg-white/10 hover:bg-[#00BEE7]/20 hover:text-[#00BEE7] hover:border-[#00BEE7]/40 text-gray-300 rounded-xl text-xs font-bold transition-all duration-150 border border-white/10"
                  >
                    {esMarca && (
                      <span className="w-1.5 h-1.5 rounded-full bg-[#00BEE7] shrink-0" />
                    )}
                    {label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Dropdown de resultados */}
          {showDropdown && query && (
            <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-100 rounded-2xl shadow-2xl z-[9999] overflow-hidden">
              {cargando && (
                <div className="p-4 space-y-3">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="flex gap-3 animate-pulse">
                      <div className="w-14 h-14 rounded-xl bg-gray-100 shrink-0" />
                      <div className="flex-1 space-y-2 py-1">
                        <div className="h-3.5 bg-gray-100 rounded-lg w-3/4" />
                        <div className="h-3 bg-gray-100 rounded-lg w-1/2" />
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {!cargando && resultadosBusqueda.length > 0 ? (
                <>
                  <div className="px-4 pt-3 pb-2 border-b border-gray-50">
                    <p className="text-xs text-gray-400 font-bold uppercase tracking-wider">
                      {resultadosBusqueda.length} resultado{resultadosBusqueda.length !== 1 ? 's' : ''} para &ldquo;{query}&rdquo;
                    </p>
                  </div>
                  <ul className="max-h-72 overflow-y-auto scroll-container divide-y divide-gray-50">
                    {resultadosBusqueda.map((resultado) => (
                      <Link
                        to={`/producto/${resultado.titulo}`}
                        state={{ id: resultado.id }}
                        key={resultado.id}
                        className="flex items-center gap-3 px-4 py-3 hover:bg-[#0f274b]/5 transition-colors duration-150"
                        onClick={() => { setFocused(false); setQuery(''); setResultadosBusqueda([]); }}
                      >
                        <div className="w-12 h-12 shrink-0 rounded-xl bg-gray-50 border border-gray-100 flex items-center justify-center overflow-hidden">
                          <img src={resultado.imagen_url} alt={resultado.titulo} className="w-12 h-12 object-contain p-1" onError={(e) => { e.currentTarget.src = 'https://dps2wxsijib8x.cloudfront.net/atst/LOGO-ATST.png'; e.currentTarget.onerror = null; }} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-bold text-[#0f274b] text-sm truncate">{resultado.titulo}</p>
                          <p className="text-gray-400 text-xs mt-0.5 line-clamp-1">
                            {stripHtmlTags(resultado.descripcion).substring(0, 80)}
                          </p>
                        </div>
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-[#00BEE7] shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                        </svg>
                      </Link>
                    ))}
                  </ul>
                  <div className="px-4 py-2.5 border-t border-gray-50 bg-gray-50/50">
                    <Link
                      to="/productos"
                      className="text-xs text-[#007fa0] font-bold hover:text-[#0f274b] transition-colors"
                      onClick={() => setFocused(false)}
                    >
                      Ver todo el catálogo →
                    </Link>
                  </div>
                </>
              ) : !cargando && busquedaRealizada && (
                <div className="py-8 px-4 flex flex-col items-center text-center">
                  <div className="w-24 h-24">
                    <LottieAnimation animationData={emptyAnimation} />
                  </div>
                  <p className="text-gray-500 text-sm font-semibold mt-1">
                    Sin resultados para <span className="font-bold text-[#0f274b]">&ldquo;{query}&rdquo;</span>
                  </p>
                  <Link to="/productos" className="mt-3 text-[#00BEE7] font-bold text-xs hover:underline" onClick={() => setFocused(false)}>
                    Ver todo el catálogo →
                  </Link>
                </div>
              )}
            </div>
          )}

        </div>

      </div>
    </div>
  );
}
